import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ContributorTable = ({ contributors, onEdit, onViewGalleries, onDelete, onRoleChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [changingRole, setChangingRole] = useState(null);

  const filteredContributors = contributors?.filter(contributor =>
    contributor?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    contributor?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleRoleChange = async (contributorId, newRole) => {
    setChangingRole(contributorId);
    try {
      await onRoleChange?.(contributorId, newRole);
    } finally {
      setChangingRole(null);
    }
  };

  const roleOptions = [
    { value: 'owner', label: 'Owner', description: 'Full system access' },
    { value: 'contributor', label: 'Contributor', description: 'Create galleries' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative w-full md:w-96">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            placeholder="Search contributors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <Button variant="default" iconName="UserPlus" iconPosition="left">
          Add Contributor
        </Button>
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contributor</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Role</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Galleries</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Active</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredContributors?.map((contributor) => (
              <tr key={contributor?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary">
                      <Image
                        src={contributor?.avatar}
                        alt={contributor?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="font-medium">{contributor?.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-muted-foreground">{contributor?.email}</td>
                <td className="py-3 px-4">
                  <div className="w-40">
                    <Select
                      options={roleOptions}
                      value={contributor?.role || 'contributor'}
                      onChange={(value) => handleRoleChange(contributor?.id, value)}
                      disabled={changingRole === contributor?.id}
                    />
                  </div>
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => onViewGalleries?.(contributor?.id)}
                    className="text-primary hover:underline font-medium"
                  >
                    {contributor?.galleryCount} galleries
                  </button>
                </td>
                <td className="py-3 px-4 text-sm text-muted-foreground">
                  {formatDate(contributor?.lastActive)}
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    contributor?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      contributor?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                    }`} />
                    {contributor?.status}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit2"
                      onClick={() => onEdit?.(contributor?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDelete?.(contributor?.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {filteredContributors?.map((contributor) => (
          <div key={contributor?.id} className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-start gap-3 mb-3">
              <Image
                src={contributor?.avatar}
                alt={contributor?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium mb-1">{contributor?.name}</h4>
                <p className="text-xs text-muted-foreground mb-2">{contributor?.email}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  contributor?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    contributor?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                  }`}></span>
                  {contributor?.status}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
              <div>
                <p className="text-muted-foreground mb-1">Galleries</p>
                <p className="font-medium data-text">{contributor?.galleryCount}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Last Active</p>
                <p className="font-medium">{formatDate(contributor?.lastActive)}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                fullWidth
                onClick={() => onViewGalleries(contributor?.id)}
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => onEdit(contributor?.id)}
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(contributor?.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContributorTable;